/**
 * ReviewCard.js — Review-platform link button (cardType "review").
 *
 * THE shared review button, used by every winery. Reviews are BE-driven
 * (reviews_information, from the tenant REVIEWS_INFORMATION prompt config) — NOT
 * hardcoded. Each item renders as a full-width `.ws-rich-review-button` with a
 * platform icon (Google / Yelp / TripAdvisor, chosen by matching the label) that
 * opens the URL in a new tab. Colours come from cards.css (themed via vars); a
 * winery overrides only where it structurally differs.
 *
 * Icon source:
 *   1. theme.google_img_url / yelp_img_url / trip_img_url (admin tenant config)
 *   2. bundled inline SVG (below) — works for every tenant with no per-tenant
 *      asset upload required (the old CDN-path fallback silently 404'd for any
 *      tenant that didn't have /assets/<tenant>/{google|yelp|trip}.webp hosted).
 *
 * BE ITEM SHAPE: { label, url, unique_id }  (also tolerates { "<label>": "<url>" }).
 *
 * REFERENCE (old code): goosecross/main.js createReviews() + googleSVG/yelpSVG/tripSVG.
 */

import { getTheme } from "../../core/configAccessors.js";

function platformOf(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("yelp")) return "yelp";
  if (l.includes("tripadvisor") || l.includes("trip")) return "trip";
  if (l.includes("google")) return "google";
  return "google"; // old createReviews defaulted to googleSVG
}

const THEME_ICON_KEY = {
  google: "google_img_url",
  yelp: "yelp_img_url",
  trip: "trip_img_url",
};

const ICON_GOOGLE = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>`;

const ICON_YELP = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#D32323"/><g fill="#ffffff"><ellipse cx="12" cy="6.2" rx="1.6" ry="3.2"/><ellipse cx="17.3" cy="9.2" rx="1.6" ry="3.2" transform="rotate(60 17.3 9.2)"/><ellipse cx="17.3" cy="14.8" rx="1.6" ry="3.2" transform="rotate(120 17.3 14.8)"/><ellipse cx="12" cy="17.8" rx="1.6" ry="3.2"/><ellipse cx="6.7" cy="14.8" rx="1.6" ry="3.2" transform="rotate(60 6.7 14.8)"/><ellipse cx="6.7" cy="9.2" rx="1.6" ry="3.2" transform="rotate(120 6.7 9.2)"/></g><circle cx="12" cy="12" r="2.6" fill="#D32323"/></svg>`;

const ICON_TRIP = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#00AF87" d="M12 3.6c-2.9 0-5.6.9-7.8 2.4H2v1.6h2.2c-.9 1-1.4 2.3-1.4 3.7 0 3.1 2.5 5.6 5.6 5.6 1.9 0 3.6-1 4.6-2.4l1-1.5 1 1.5c1 1.5 2.7 2.4 4.6 2.4 3.1 0 5.6-2.5 5.6-5.6 0-1.4-.5-2.7-1.4-3.7H22V6h-2.2C17.6 4.5 14.9 3.6 12 3.6zM8.4 13.5c-1.9 0-3.5-1.6-3.5-3.5S6.5 6.5 8.4 6.5s3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm7.2 0c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z"/><circle cx="8.4" cy="10" r="1.6" fill="#00AF87"/><circle cx="15.6" cy="10" r="1.6" fill="#00AF87"/></svg>`;

const ICONS = { google: ICON_GOOGLE, yelp: ICON_YELP, trip: ICON_TRIP };

export default class ReviewCard {
  render(item) {
    let label = item.label || item.cta_text || item.title || "";
    let url = item.url || item.cta_link || item.link || "";
    // Tolerate the { "<label>": "<url>" } dict shape some BE configs use.
    if (!url) {
      for (const [k, v] of Object.entries(item)) {
        if (k === "unique_id") continue;
        if (typeof v === "string" && /^https?:/i.test(v)) {
          label = k;
          url = v;
          break;
        }
      }
    }
    if (!url) return null;

    const a = document.createElement("a");
    a.className = "ws-rich-review-button";
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";

    const platform = platformOf(label);
    const themeIconUrl = getTheme(THEME_ICON_KEY[platform]);
    if (themeIconUrl) {
      const img = document.createElement("img");
      img.src = themeIconUrl;
      img.alt = "";
      img.width = 16;
      img.height = 16;
      img.loading = "lazy";
      a.appendChild(img);
    } else {
      const icon = document.createElement("span");
      icon.className = "ws-rich-review-icon";
      icon.innerHTML = ICONS[platform];
      a.appendChild(icon);
    }

    const span = document.createElement("span");
    span.textContent = label;
    a.appendChild(span);
    return a;
  }
}
